/**
 * Blog archive — AJAX category refinement + numbered pagination.
 *
 * Chips and pager buttons fetch the matching page of posts and swap the grid
 * in place (no full reload). The active category + page are mirrored to the
 * URL (?cat=slug&paged=N) so views are shareable, restored on load, and
 * survive back/forward. The grid is rendered server-side for the initial
 * request, so this only enhances an already-working page — and degrades
 * gracefully if the AJAX config is absent.
 */
export function initBlogFilter() {
	const chips = document.getElementById('chada-blog-chips');
	const grid = document.getElementById('chada-blog-grid');
	const pager = document.getElementById('chada-blog-pagination');
	if (!grid || !pager || typeof window.chadaBlog === 'undefined') return;

	const section = grid.closest('.grid-sec') || grid;
	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	// Keep the spinner up long enough to register, even on a fast local response.
	const MIN_SPINNER_MS = 400;

	// Source of truth; seeded from the server-rendered pager data attributes.
	const state = {
		cat: pager.dataset.cat || '',
		page: parseInt(pager.dataset.page || '1', 10),
		maxPages: parseInt(pager.dataset.max || '1', 10),
		featured: pager.dataset.featured || '',
	};
	let busy = false;

	// Mark the chip matching this slug active ('' / null = the "All" chip).
	function setActive(slug) {
		if (!chips) return;
		chips.querySelectorAll('.chip').forEach((chip) => {
			chip.classList.toggle('active', (chip.dataset.cat || '') === (slug || ''));
		});
	}

	// Bring the refined results into view, just below the sticky header.
	function scrollToResults() {
		const header = document.getElementById('site-header');
		const offset = (header ? header.offsetHeight : 0) + 16;
		const top = section.getBoundingClientRect().top + window.scrollY - offset;
		window.scrollTo({ top: Math.max(top, 0), behavior: reduceMotion ? 'auto' : 'smooth' });
	}

	// Reflect the active category + page in the address bar without reloading.
	function syncUrl() {
		const url = new URL(window.location.href);
		if (state.cat) {
			url.searchParams.set('cat', state.cat);
		} else {
			url.searchParams.delete('cat');
		}
		if (state.page > 1) {
			url.searchParams.set('bpage', String(state.page));
		} else {
			url.searchParams.delete('bpage');
		}
		window.history.pushState({ cat: state.cat, page: state.page }, '', url);
	}

	function pageButton(label, page, { active = false, disabled = false } = {}) {
		const button = document.createElement('button');
		button.type = 'button';
		button.className = 'pgbtn' + (active ? ' active' : '');
		button.innerHTML = label;
		if (active) button.setAttribute('aria-current', 'page');
		if (disabled) {
			button.disabled = true;
		} else {
			button.addEventListener('click', () => {
				if (page !== state.page) {
					state.page = page;
					loadPosts({ scroll: true });
				}
			});
		}
		return button;
	}

	// Build the numbered pager (windowed: first, last, current ±1, ellipses).
	function renderPager() {
		pager.innerHTML = '';
		const pages = state.maxPages;
		if (pages <= 1) {
			pager.style.display = 'none';
			return;
		}
		pager.style.display = '';

		pager.appendChild(pageButton('‹ Prev', state.page - 1, { disabled: state.page <= 1 }));

		const items = [];
		const window_ = 1;
		for (let i = 1; i <= pages; i++) {
			if (i === 1 || i === pages || (i >= state.page - window_ && i <= state.page + window_)) {
				items.push(i);
			} else if (items[items.length - 1] !== 'dots') {
				items.push('dots');
			}
		}
		items.forEach((item) => {
			if (item === 'dots') {
				const dots = document.createElement('span');
				dots.className = 'pgdots';
				dots.setAttribute('aria-hidden', 'true');
				dots.textContent = '…';
				pager.appendChild(dots);
			} else {
				pager.appendChild(pageButton(String(item), item, { active: item === state.page }));
			}
		});

		pager.appendChild(pageButton('Next ›', state.page + 1, { disabled: state.page >= pages }));
	}

	// Fetch + swap the grid for the current { cat, page } state.
	async function loadPosts({ scroll = true } = {}) {
		if (busy) return;
		busy = true;
		section.classList.add('is-loading');
		grid.setAttribute('aria-busy', 'true');

		const params = new URLSearchParams({
			action: 'chada_filter_posts',
			nonce: window.chadaBlog.nonce,
			cat: state.cat,
			paged: String(state.page),
			featured: state.featured,
		});
		const startedAt = performance.now();

		try {
			const res = await fetch(window.chadaBlog.ajaxurl + '?' + params.toString(), { credentials: 'same-origin' });
			const json = await res.json();
			// Guarantee a minimum spinner duration so it doesn't just flash.
			const wait = MIN_SPINNER_MS - (performance.now() - startedAt);
			if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait));

			if (json && json.success) {
				grid.innerHTML = json.data.html;
				state.maxPages = parseInt(json.data.max_pages, 10) || 1;
				if (state.page > state.maxPages) state.page = state.maxPages;
				renderPager();
				syncUrl();
				if (scroll) scrollToResults();
			}
		} catch (err) {
			/* leave the current grid in place on failure */
		} finally {
			section.classList.remove('is-loading');
			grid.setAttribute('aria-busy', 'false');
			busy = false;
		}
	}

	if (chips) {
		chips.addEventListener('click', (e) => {
			const chip = e.target.closest('.chip');
			if (!chip || busy) return;
			state.cat = chip.dataset.cat || '';
			state.page = 1;
			setActive(state.cat);
			loadPosts({ scroll: true });
		});
	}

	// Back/forward should restore the category + page from the URL.
	window.addEventListener('popstate', () => {
		const url = new URL(window.location.href);
		state.cat = url.searchParams.get('cat') || '';
		state.page = Math.max(1, parseInt(url.searchParams.get('bpage') || '1', 10));
		setActive(state.cat);
		loadPosts({ scroll: false });
	});

	// The server already rendered the right page; just build the pager.
	renderPager();
}
